import { FC, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { Text } from '../../../../components/Text';
import { STYLE_VARS } from '../../../../constants/style-vars';
import { useIsDesktop } from '../../../../hooks/useIsDeskTop';
import { myAccountStatementStyles as styled } from './AccountTable.styled';
import { AccountTableProps } from './AccountTable.types';

export const AccountTable: FC<AccountTableProps> = ({
  data,
  totalBalance,
  totalCharges,
}) => {
  const isDesktop = useIsDesktop();
  const renderHeaderContent = useMemo(() => {
    if (isDesktop()) {
      const contents = [
        {
          title: 'Date',
          width: '20%',
        },
        {
          title: 'Description',
          width: '35%',
        },
        {
          title: 'Charges',
          width: '15%',
        },
        {
          title: 'Credit',
          width: '15%',
        },
        {
          title: 'Balances',
          width: '15%',
        },
      ];
      return contents.map((content, index) => (
        <View
          style={{
            width: content.width,
            alignItems: index <= 1 ? 'flex-start' : 'center',
            paddingLeft: index <= 1 ? 20 : 0,
          }}
          key={content.title}
        >
          <Text style={[styled.th_text, { fontSize: 15 }]}>
            {content.title}
          </Text>
        </View>
      ));
    } else {
      const contents = [
        {
          title: 'Transaction',
          width: '30%',
        },
        {
          title: 'Charges',
          width: '25%',
        },
        {
          title: 'Credit',
          width: '20%',
        },
        {
          title: 'Balances',
          width: '25%',
        },
      ];
      return contents.map((content, index) => (
        <View
          style={{
            width: content.width,
            alignItems: index == 0 ? 'flex-start' : 'center',
            paddingLeft: index == 0 ? 12 : 0,
          }}
          key={content.title}
        >
          <Text style={styled.th_text}>{content.title}</Text>
        </View>
      ));
    }
  }, [isDesktop]);

  const renderBodyContent = useMemo(() => {
    if (isDesktop()) {
      return data.map((statement, index) => (
        <View style={styled.tbody} key={index}>
          <View
            style={[
              styled.tbody_content,
              {
                width: '20%',
                paddingHorizontal: 20,
              },
            ]}
          >
            <Text style={[styled.table_text, { fontSize: 15 }]}>
              {statement.date}
            </Text>
          </View>
          <View
            style={[
              styled.tbody_content,
              {
                width: '35%',
                backgroundColor: STYLE_VARS.fccLightGrey,
                paddingHorizontal: 20,
              },
            ]}
          >
            <Text style={[styled.table_text, { fontSize: 15 }]}>
              {statement.description}
            </Text>
          </View>
          <View
            style={[
              styled.tbody_content,
              {
                width: '15%',
                alignItems: 'center',
              },
            ]}
          >
            <Text style={[styled.table_text, { fontSize: 15 }]}>
              {statement.amount && statement.amount > 0
                ? statement.amount?.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })
                : 0}
            </Text>
          </View>
          <View
            style={[
              styled.tbody_content,
              {
                width: '15%',
                alignItems: 'center',
                backgroundColor: STYLE_VARS.fccLightGrey,
              },
            ]}
          >
            <Text style={[styled.table_text, { fontSize: 15 }]}>
              {statement.amount && statement.amount < 0
                ? statement.amount?.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })
                : 0}
            </Text>
          </View>
          <View
            style={[
              styled.tbody_content,
              {
                width: '15%',
                alignItems: 'center',
              },
            ]}
          >
            <Text style={[styled.table_text, { fontSize: 15 }]}>
              {Number(statement.runbal)?.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
              })}
            </Text>
          </View>
        </View>
      ));
    } else {
      return data.map((statement, index) => (
        <View style={styled.tbody} key={index}>
          <View
            style={[
              styled.tbody_content,
              {
                width: '100%',
                backgroundColor: STYLE_VARS.fccLightGrey,
                paddingHorizontal: 20,
              },
            ]}
          >
            <Text style={styled.table_text}>{statement.description}</Text>
          </View>
          <View
            style={[
              styled.tbody_content,
              {
                width: '30%',
                paddingHorizontal: 10,
              },
            ]}
          >
            <Text style={styled.table_text}>{statement.date}</Text>
          </View>
          <View
            style={[
              styled.tbody_content,
              {
                width: '25%',
                alignItems: 'center',
              },
            ]}
          >
            <Text style={styled.table_text}>
              {statement.amount && statement.amount > 0
                ? statement.amount?.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })
                : 0}
            </Text>
          </View>
          <View
            style={[
              styled.tbody_content,
              {
                width: '20%',
                alignItems: 'center',
                backgroundColor: STYLE_VARS.fccLightGrey,
              },
            ]}
          >
            <Text style={styled.table_text}>
              {statement.amount && statement.amount < 0
                ? statement.amount?.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })
                : 0}
            </Text>
          </View>
          <View
            style={[
              styled.tbody_content,
              {
                width: '25%',
                alignItems: 'center',
              },
            ]}
          >
            <Text style={styled.table_text}>
              {Number(statement.runbal)?.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
              })}
            </Text>
          </View>
        </View>
      ));
    }
  }, [data, isDesktop]);

  const renderFooterContent = useMemo(() => {
    if (isDesktop()) {
      return (
        <View style={styled.tfoot}>
          <View style={{ flexDirection: 'row' }}>
            <View
              style={[
                styled.tbody_content,
                {
                  width: '55%',
                  paddingHorizontal: 20,
                },
              ]}
            >
              <Text style={[styled.table_text, { fontSize: 15 }]}>
                Total Charges
              </Text>
            </View>
            <View
              style={[
                styled.tbody_content,
                {
                  width: '15%',
                  alignItems: 'center',
                },
              ]}
            >
              <Text style={[styled.table_text, { fontSize: 15 }]}>
                {totalCharges?.toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                })}
              </Text>
            </View>
            <View
              style={[
                styled.tbody_content,
                {
                  width: '15%',
                  alignItems: 'center',
                },
              ]}
            />
            <View
              style={[
                styled.tbody_content,
                {
                  width: '15%',
                  alignItems: 'center',
                },
              ]}
            />
          </View>
          <View
            style={{
              flexDirection: 'row',
              backgroundColor: STYLE_VARS.fccLightGrey,
            }}
          >
            <View
              style={[
                styled.tbody_content,
                {
                  width: '55%',
                  paddingHorizontal: 20,
                },
              ]}
            >
              <Text style={[styled.table_text, { fontSize: 15 }]}>
                Total Balance
              </Text>
            </View>
            <View
              style={[
                styled.tbody_content,
                {
                  width: '15%',
                  alignItems: 'center',
                },
              ]}
            />
            <View
              style={[
                styled.tbody_content,
                {
                  width: '15%',
                  alignItems: 'center',
                },
              ]}
            />
            <View
              style={[
                styled.tbody_content,
                {
                  width: '15%',
                  alignItems: 'center',
                },
              ]}
            >
              <Text style={[styled.table_text, { fontSize: 15 }]}>
                {totalBalance?.toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                })}
              </Text>
            </View>
          </View>
        </View>
      );
    } else {
      return (
        <View style={styled.tfoot}>
          <View style={{ flexDirection: 'row' }}>
            <View
              style={[
                styled.tbody_content,
                {
                  width: '30%',
                  paddingHorizontal: 20,
                },
              ]}
            >
              <Text style={styled.table_text}>Total Charges</Text>
            </View>
            <View
              style={[
                styled.tbody_content,
                {
                  width: '25%',
                  alignItems: 'center',
                },
              ]}
            >
              <Text style={styled.table_text}>
                {totalCharges?.toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                })}
              </Text>
            </View>
            <View
              style={[
                styled.tbody_content,
                {
                  width: '45%',
                  alignItems: 'center',
                },
              ]}
            />
          </View>
          <View
            style={{
              paddingHorizontal: 20,
              paddingVertical: 15,
              flexDirection: 'row',
              justifyContent: 'space-between',
              backgroundColor: STYLE_VARS.fccLightGrey,
            }}
          >
            <Text style={styled.table_text}>Total Balance</Text>
            <Text style={styled.table_text}>
              {totalBalance?.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
              })}
            </Text>
          </View>
        </View>
      );
    }
  }, [totalCharges, totalBalance, isDesktop]);

  return (
    <View style={styled.table}>
      <View style={styled.thead}>{renderHeaderContent}</View>
      <View>{renderBodyContent}</View>
      <View>{renderFooterContent}</View>
    </View>
  );
};
